import { ChangeDetectionStrategy, Component, computed, inject, SecurityContext, signal, TrackByFunction } from '@angular/core'

import { Router, Event, NavigationEnd, RouterLink } from '@angular/router'
import { filter, take } from 'rxjs/operators'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ModalDealerSearchComponent } from '@appShared/components/dealer-search/modal-dealer-search.component'
import { AppSignals, AuthService, CommonService, DealerService } from '@appShared/services'
import { INotification } from '@appShared/interfaces/[Interface-based]/notification.interface'
import {
   DealerNotificationType_,
   NotificationLevel_
} from '@appShared/services/lookup/[CodeGen]'
import { SITE_DIRECTORY_ROUTES, RouteModel } from '@appShared/site-directory.routes'
import { GEN2_ROUTES } from '@appShared/gen2.routes'
import { EXTERNAL_URIS } from '@appShared/external.uris'
import { environment } from '@appEnvironments/environment'

@Component({
   selector: 'app-nav-menu',
   templateUrl: './nav-menu.component.html',
   styleUrls: ['./nav-menu.component.less'],
   imports: [RouterLink],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavMenuComponent {
   private readonly _commonService = inject(CommonService)
   private readonly _dealerService = inject(DealerService)
   private readonly _appSignals = inject(AppSignals)
   private readonly _authService = inject(AuthService)
   private readonly _modalService = inject(NgbModal)

   // Routes and URIs - make them readonly to prevent accidental modification
   private _internalRoutes = SITE_DIRECTORY_ROUTES.internal
   readonly dashboardUri = `/${SITE_DIRECTORY_ROUTES.internal.dashboard.uri}`
   readonly routes = SITE_DIRECTORY_ROUTES
   readonly a4dAppRoute = this.routes.a4d_app
   readonly a4dAppRouteUri = `/${this.a4dAppRoute.uri}`
   readonly captureAppRoute = this.routes.capture_app
   readonly captureAppRouteUri = `/${this.captureAppRoute.uri}`
   readonly salesSchoolRegistrationRoute = this.routes.internal.sales_school_registration
   private _accountRoutes = this._internalRoutes.account
   readonly remitOnlineUri = `/${this._accountRoutes.remittance.uri}`
   readonly remitOnlineTitle = this._accountRoutes.remittance.title
   readonly remitOnlineRoute = this.routes.internal.account.remittance
   readonly manageAccountRoute = this.routes.internal.account.manage
   readonly accountActivityUri = `/${this._accountRoutes.account_activity.uri}`
   readonly accountActivityTitle = this._accountRoutes.account_activity.title
   readonly personalSalesReportedUri = `/${this._accountRoutes.personal_sales_reported.uri}`
   readonly personalSalesReportedTitle = this._accountRoutes.personal_sales_reported.title
   readonly orgRecruitingUri = `/${this._accountRoutes.org_recruiting.uri}`
   readonly orgRecruitingTitle = this._accountRoutes.org_recruiting.title
   readonly orgSalesReportedUri = `/${this._accountRoutes.org_sales_reported.uri}`
   readonly orgSalesReportedTitle = this._accountRoutes.org_sales_reported.title
   readonly costCenterReportsUri = `/${this._accountRoutes.cost_center_reports.uri}`
   readonly costCenterReportsTitle = this._accountRoutes.cost_center_reports.title
   /* ERP-backed Gen3 reports are hidden until the ERP is live in this environment (environment flag) */
   readonly acumaticaIsLive = environment.acumaticaIsLive
   /** Org Recruiting / Org Sales Reported only for org leaders, assistant OLs, managers and DSMs of the selected dealer. */
   readonly canViewOrgReporting = this._dealerService.orgReportingAccess
   readonly merchandiseReturnsRoute = this.routes.internal.merchandise_returns
   readonly gen2Routes = GEN2_ROUTES
   readonly external_uris = EXTERNAL_URIS

   // UI state
   isAuthenticated = false
   isExpanded = false

   // Signals
   readonly platformIsAndroid = this._appSignals.platformIsAndroid
   readonly currentProfile = computed(() => this._appSignals.currentProfile())
   readonly salesPortalUri = computed(() => this._appSignals.salesPortalUri())

   // Dealer-related signals
   readonly selectedDealer = computed(() => this._dealerService.selectedDealer())

   /* dealer photo comes from /api/dealer/{sourceId}/photo?placeholder=false: 404 when none is on file, so the initials show instead */
   private readonly _photoFailedForSourceId = signal<string | null>(null)
   readonly photoFailed = computed(() => {
      const sourceId = this.selectedDealer()?.contact?.sourceId
      return !!sourceId && this._photoFailedForSourceId() === sourceId
   })
   readonly dealerInitials = computed(() => {
      const contact = this.selectedDealer()?.contact
      const first = (contact?.preferredName || contact?.firstName || '').trim()
      const last = (contact?.lastName || '').trim()
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()
   })

   onDealerPhotoError(): void {
      this._photoFailedForSourceId.set(this.selectedDealer()?.contact?.sourceId ?? null)
   }

   // Notifications
   private readonly _notifications = computed(() => this._dealerService.notifications())
   readonly filteredNotifications = computed(() => {
      /* show no more than 5 of "un"-cleared notifications*/
      const notifications = this._notifications().filter(notification => !notification.isCleared).slice(0, 5)

      /* set alert level css */
      notifications.forEach(notification => {
         notification['css'] = this.getNotificationCssClass(notification.level)
         notification['icon'] = this.getNotificationIcon(notification.level)
         notification['messageMarkup'] = this._commonService.transform(notification.message, SecurityContext.HTML)
      })

      return notifications
   })

   readonly notificationCountDisplay = computed(() => {
      const filteredCount = this._notifications().filter(notification => !notification.isCleared).length
      return filteredCount > 5 ? '5+' : '' + filteredCount
   })

   readonly showNotificationCount = computed(() => this.filteredNotifications().length > 0)
   readonly notificationLevel_ = NotificationLevel_

   constructor(router: Router) {
      router.events
         .pipe(
            takeUntilDestroyed(),
            filter((routerEvent: Event) => routerEvent instanceof NavigationEnd)
         )
         .subscribe((routerEvent: NavigationEnd) => {
            const url = routerEvent.url.toLowerCase()
            this.isAuthenticated = !url.startsWith('/student-dealer')
         })
   }

   /**
    * Navigates to external URI
    * @param uri Optional URI to navigate to
    */
   goToExternal(uri?: string): void {
      this._appSignals.navigateToExternal(uri)
   }

   /**
    * Navigates to portal route
    * @param route Optional route to navigate to
    */
   goToPortal(route?: RouteModel): void {
      this._dealerService.navigateToGen2(route?.uri)
   }

   /**
    * Collapses the expanded menu
    */
   collapse(): void {
      this.isExpanded = false
   }

   /**
    * Toggles menu expansion
    */
   toggle(): void {
      this.isExpanded = !this.isExpanded
   }

   /**
    * Opens dealer search modal
    */
   searchDealers(): void {
      ModalDealerSearchComponent.open(this._modalService)
   }

   /**
    * Logs out the current user
    */
   logout(): void {
      this._authService.logout()
   }

   /**
    * Track by function for notification list
    */
   trackByNotificationId: TrackByFunction<INotification> = (_, notification) => notification.id

   /**
    * Acknowledges a notification
    * @param notification The notification to acknowledge
    */
   acknowledgeNotification(notification: INotification): void {
      this._dealerService.acknowledgeNotifications([notification.id])
         .pipe(take(1))
         .subscribe(() => {
            this._resolveNotificationAction(notification)
         })
   }

   /**
    * Navigate to see all notifications
    */
   seeAllNotifications(): void {
      this.goToPortal(this.gen2Routes.settings_notifications)
   }

   /**
    * Clears all notifications
    */
   clearAllNotifications(): void {
      const notificationIds = this.filteredNotifications().map(n => n.id)

      if (notificationIds.length) {
         this._dealerService.acknowledgeNotifications(notificationIds)
            .pipe(take(1))
            .subscribe(() => {
               this._dealerService.refreshNotifications()
            })
      }
   }

   /**
    * Gets the CSS class for a notification based on its level
    * @param level The notification level
    * @returns CSS class string
    */
   private getNotificationCssClass(level: NotificationLevel_): string {
      /* tone classes are styled in nav-menu.component.less; the color meaning matches the old alert classes */
      switch (level) {
         case NotificationLevel_.Informational:
            return 'tone-info'
         case NotificationLevel_.Important:
            return 'tone-danger'
         case NotificationLevel_.Urgent:
            return 'tone-warning'
         default:
            return 'tone-info'
      }
   }

   /**
    * Font Awesome icon for a notification level
    * @param level The notification level
    */
   private getNotificationIcon(level: NotificationLevel_): string {
      switch (level) {
         case NotificationLevel_.Important:
            return 'fa-circle-exclamation'
         case NotificationLevel_.Urgent:
            return 'fa-triangle-exclamation'
         default:
            return 'fa-circle-info'
      }
   }

   /**
    * Resolves notification action based on notification type
    * @param notification The notification to resolve
    */
   private _resolveNotificationAction(notification: INotification): void {
      if (!notification) return

      const notificationType: DealerNotificationType_ = notification.type

      const customerServiceTypes = [
         DealerNotificationType_.CustomerServiceContact,
         DealerNotificationType_.CustomerServiceCancel,
         DealerNotificationType_.CustomerServicePendingRefund,
         DealerNotificationType_.ScheduledPaymentDecline,
         DealerNotificationType_.PaymentCardMaintenanceCancel
      ]

      if (customerServiceTypes.includes(notificationType)) {
         const keyEncrypted = notification['keyEncrypted']
         const route = `${this.gen2Routes.record_of_sale_customer.uri}?id=${keyEncrypted}`
         this._dealerService.navigateToGen2(route)
         return
      }

      if (notificationType === DealerNotificationType_.MissingStats) {
         this.goToPortal(this.gen2Routes.e_bar)
         return
      }

      this._dealerService.refreshNotifications()
   }
}
